import React, { useEffect, useState, useRef } from "react";
import {
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TablePagination,
    Paper,
    IconButton,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Toolbar,
    Checkbox,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import readXlsxFile from "read-excel-file";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import LoadingComponent from "../CommonComponents/loadinComponent";
import ExportPdf from "../CommonComponents/exportPdf";
import { siteName } from "../../config/config";
import { history } from "../../history";
import TopView from "../CommonComponents/topView";
import { decryptAesObj } from "../CommonComponents/AESencryptdecrypt";
import { ScheduleMasterAction } from "../../actions/ScheduleMaster";
import { VehicleAction } from "../../actions/vehicle";
import moment from "moment";
// import { SiteAction } from "../../actions/site";
import { siteAction } from "../../actions/site_action";

// ── Table headers ─────────────────────────────────────────────────────────────
const headCells = [
    { id: "checkbox", label: "", minWidth: 40 },
    { id: "sno", label: "S.No", minWidth: 60 },
    { id: "vehicleNumber", label: "Vehicle", minWidth: 120 },
    { id: "fplName", label: "FPL Name", minWidth: 200 },
    { id: "day", label: "Day", minWidth: 120 },
    { id: "fplTime", label: "FPL Time", minWidth: 180 },
    { id: "delivery", label: "Delivery Time", minWidth: 180 },
    { id: "action", label: "Action", minWidth: 80 },
];

const ALLOWED_EXCEL_TYPES = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const labelHeader = ["S. No", "Vehicle", "FPL Name", "Day", "FPL Time", "Delivery Time"];

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const LAT_LONG_COLS = [3, 5, 7, 9, 11, 13, 15];

const DAY_COLOR = {
    MONDAY: { bg: "#E3F2FD", color: "#1565C0", border: "#90CAF9" },
    TUESDAY: { bg: "#F3E5F5", color: "#6A1B9A", border: "#CE93D8" },
    WEDNESDAY: { bg: "#E8F5E9", color: "#2E7D32", border: "#A5D6A7" },
    THURSDAY: { bg: "#FFF3E0", color: "#E65100", border: "#FFCC80" },
    FRIDAY: { bg: "#FCE4EC", color: "#880E4F", border: "#F48FB1" },
    SATURDAY: { bg: "#E0F7FA", color: "#006064", border: "#80DEEA" },
    SUNDAY: { bg: "#FFF8E1", color: "#F57F17", border: "#FFE082" },
};

const formatTime = (t) => t ? moment(t, "HH:mm:ss").format("hh:mm A") : "—";

// ── Excel validation (front-end gate 1) ───────────────────────────────────────
const validateExcelFile = async (file) => {
    const LAT_LONG_COLS = [3, 5, 7, 9, 11, 13, 15];  // LatLong column indices
    const ADDRESS_COLS = [2, 4, 6, 8, 10, 12, 14];   // Address column indices
    const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    try {
        const rows = await readXlsxFile(file);
        const dataRows = rows.slice(1).filter(row =>
            row.some(cell => String(cell || "").trim() !== "")
        );

        if (dataRows.length === 0) {
            return [{
                rowNum: "—", tankerNo: "—", status: "error",
                errors: ["No data found"], reason: "Sheet has no data rows.",
            }];
        }

        return dataRows.map((row, i) => {
            const rowNum = i + 2;
            const tankerNo = String(row[0] || "").trim();
            const errors = [];

            if (!tankerNo) errors.push("Tanker No is empty");

            DAY_NAMES.forEach((day, dayIdx) => {
                const addressColIdx = ADDRESS_COLS[dayIdx];
                const latLongColIdx = LAT_LONG_COLS[dayIdx];

                const addressVal = String(row[addressColIdx] || "").trim();
                const latLongVal = String(row[latLongColIdx] || "").trim();

                // ✅ ONLY validate LatLong if address exists
                if (!addressVal) {
                    if (latLongVal) {
                        errors.push(`${day}: Address is empty but LatLong is provided`);
                    }
                    return;  // Skip LatLong validation
                }

                // Now check LatLong (only because address exists)
                if (!latLongVal) {
                    errors.push(`${day} LatLong is empty (but address exists)`);
                    return;
                }

                // Validate format
                if (!latLongVal.includes(",")) {
                    errors.push(`${day} LatLong must be comma-separated (e.g. 28.71,77.24)`);
                    return;
                }

                const parts = latLongVal.split(",");
                if (parts.length !== 2) {
                    errors.push(`${day} LatLong must have exactly one comma`);
                    return;
                }

                const [latStr, lonStr] = parts.map(p => p.trim());

                // Check decimal points
                if (!latStr.includes(".")) errors.push(`${day} Latitude must have a decimal point`);
                if (!lonStr.includes(".")) errors.push(`${day} Longitude must have a decimal point`);

                // Check numeric + range
                const lat = parseFloat(latStr);
                const lon = parseFloat(lonStr);

                if (isNaN(lat) || isNaN(lon)) {
                    errors.push(`${day} LatLong has non-numeric values`);
                    return;
                }
                if (lat < -90 || lat > 90) errors.push(`${day} Latitude ${lat} out of range`);
                if (lon < -180 || lon > 180) errors.push(`${day} Longitude ${lon} out of range`);
            });

            return {
                rowNum,
                tankerNo: tankerNo || "—",
                status: errors.length === 0 ? "valid" : "error",
                errors,
                reason: errors.length === 0 ? "✅ All checks passed" : errors.join("; "),
            };
        });
    } catch (err) {
        console.error("❌ Error:", err);
        return [{
            rowNum: "—", tankerNo: "—", status: "error",
            errors: [], reason: `Parse error: ${err?.message || String(err)}`,
        }];
    }
};

// ── Toolbar ───────────────────────────────────────────────────────────────────
function ScheduleToolbar({ vehicleList, selectedVehicle, setSelectedVehicle, selectedCount, fplList, selectedFpl, setSelectedFpl, }) {
    return (
        <Toolbar style={{ marginLeft: "-8px" }}>
            <Grid container spacing={2} className="mt-1" alignItems="center">
                <Grid item xs={2}>
                    <Typography className="font14 greyfirstheading" gutterBottom variant="subtitle1">
                        Vehicle Number
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                        <Autocomplete
                            id="schedule-vehicle-select"
                            className="user"
                            options={vehicleList}
                            disablePortal
                            getOptionLabel={(vehicle) =>
                                vehicle ? `${vehicle.number} (${vehicle.id})` : ""
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedVehicle}
                            onChange={(event, newValue) => setSelectedVehicle(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" margin="dense" placeholder="Select vehicle" />
                            )}
                        />
                    </Typography>
                </Grid>
                {/* <Grid item xs={2}>
                    <Typography className="font14 greyfirstheading" gutterBottom variant="subtitle1">
                        FPL Name
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                        <Autocomplete
                            id="schedule-fpl-select"
                            className="user"
                            options={fplList}
                            disablePortal
                            getOptionLabel={(fpl) =>
                                fpl ? `${fpl.name}` : ""
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedFpl}
                            onChange={(event, newValue) => setSelectedFpl(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" margin="dense" placeholder="Select FPL" />
                            )}
                        />
                    </Typography>
                </Grid> */}
            </Grid>
        </Toolbar>
    );
}

// ── Table head ────────────────────────────────────────────────────────────────
function EnhancedTableHead({ onSelectAllClick, numSelected, rowCount }) {
    return (
        <TableHead className="theadbg border">
            <TableRow>
                <TableCell style={{ minWidth: 40 }} padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                    />
                </TableCell>
                {headCells.slice(1).map((cell) => (
                    <TableCell key={cell.id} style={{ color: "#6E6A76", minWidth: cell.minWidth }}>
                        {cell.label}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const useStyles = makeStyles((theme) => ({
    paper: { width: "101%", marginBottom: theme.spacing(2) },
    table: { minWidth: 750 },
    deleteBtn: { color: "#f44336", "&:hover": { background: "#fff5f5" } },
}));

// ── Main component ────────────────────────────────────────────────────────────
function ScheduleMaster() {
    const classes = useStyles();
    const fileInputRef = useRef(null);

    // table
    const [userlist, setUserList] = useState([]);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isLoading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    // vehicle filter
    const [vehicleList, setVehicleList] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [fplList, setFplList] = useState([]);
    const [selectedFpl, setSelectedFpl] = useState(null);

    // ── Multi-select checkboxes ────────────────────────────────────────────────
    const [selectedTrips, setSelectedTrips] = useState(new Set());

    // bulk delete
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    // import modal
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importError, setImportError] = useState("");
    const [importing, setImporting] = useState(false);

    // ── gate 1: front-end validation results ──────────────────────────────────
    const [validationResults, setValidationResults] = useState([]);
    const [showValidation, setShowValidation] = useState(false);

    // ── gate 2: server upload result ──────────────────────────────────────────
    // shape: { successCount: N, failedRows: ["Row 4: Vehicle not found", ...] }
    const [uploadResult, setUploadResult] = useState(null);

    // delete
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [exportDataPdf, setExportDataPdf] = useState([]);

    const permissionAccess = decryptAesObj(localStorage.getItem("data"));
    const addSitePermission = permissionAccess.webPermissionDTOList.filter(p => p.name === "CREATE_SITE");
    const pathname = history.location?.pathname || "";
    const isFpl = pathname.split("/")[1] === "fpllist";


    const fetchFplList = async () => {

        try {
            const d = decryptAesObj(localStorage.getItem("data"));
            const response = await siteAction.getSite({
                pageNo: 0,
                pageSize: 0,
                companyId: d.companyId,
                search: "",
                status: "",
                siteId: "",
                siteTypeId: "",
                notFetchEmergencies: true,
                type: "FPL",
                fetchCordinates: false,
            });

            if (response && Array.isArray(response) && response.length > 0) setFplList(response || []);
        } catch (e) {
            setFplList([]);
        }
    };


    const fetchVehicleList = async () => {
        try {
            const d = decryptAesObj(localStorage.getItem("data"));
            const response = await VehicleAction.fetchVehicleList({
                pageNo: 0, pageSize: 0, forDropDown: true,
                companyId: d.companyId, number: null,
                vehicleType: "", driver: "",
                siteIds: d.sites?.map(s => s.id) || [],
                userId: d.id,
            });
            setVehicleList(response || []);
        } catch (e) { setVehicleList([]); }
    };

    const fetchScheduleList = () => {
        setLoading(true);
        ScheduleMasterAction.fetchScheduleMasterList({
            vehicleId: selectedVehicle?.id || "",
            // vehicleNumber: selectedVehicle?.number || "",
            // fplId: selectedFpl?.id || "",
            pageNo,
            pageSize,
        }).then((response) => {

            const list = response?.data?.data?.scheduleTrips;
            const total = response?.data?.data?.totalPages || list?.length || 0;
            setUserList(Array.isArray(list) ? list : []);
            setTotalCount(total);
            setLoading(false);
        }).catch(() => { setUserList([]); setTotalCount(0); setLoading(false); });
    };

    const visibleRows = userlist.slice(0, pageSize);

    // ── Checkbox handlers ──────────────────────────────────────────────────────
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = new Set(userlist.map(item => item.id));
            setSelectedTrips(newSelected);
        } else {
            setSelectedTrips(new Set());
        }
    };

    const handleSelectClick = (tripId) => {
        const newSelected = new Set(selectedTrips);
        if (newSelected.has(tripId)) {
            newSelected.delete(tripId);
        } else {
            newSelected.add(tripId);
        }
        setSelectedTrips(newSelected);
    };

    // ── delete single ──────────────────────────────────────────────────────────
    const handleDeleteClick = (item) => {
        setDeleteTarget({ id: item.id, label: `${item.fplName || "—"} · ${item.day || ""}` });
        setDeleteOpen(true);
    };

    const handleDeleteClose = () => {
        if (deleting) return;
        setDeleteOpen(false);
        setDeleteTarget(null);
    };

    const handleDeleteConfirm = () => {
        if (!deleteTarget) return;
        setDeleting(true);
        const userId = decryptAesObj(localStorage.getItem("data")).id
        ScheduleMasterAction.deleteScheduleMaster({ scheduleTripId: deleteTarget.id ,userId})
            .then((res) => {
                if (res?.status === 200) {
                    fetchScheduleList();
                    setSelectedTrips(new Set());
                }
                setDeleting(false);
                setDeleteOpen(false);
                setDeleteTarget(null);
            })
            .catch(() => { setDeleting(false); setDeleteOpen(false); setDeleteTarget(null); });
    };

    // ── delete multiple ────────────────────────────────────────────────────────
    const handleBulkDeleteClick = () => {
        if (selectedTrips.size === 0) return;
        setBulkDeleteOpen(true);
    };

    const handleBulkDeleteClose = () => {
        if (bulkDeleting) return;
        setBulkDeleteOpen(false);
    };
    

    const handleBulkDeleteConfirm = () => {
        if (selectedTrips.size === 0) return;
        setBulkDeleting(true);

        const tripIds = Array.from(selectedTrips);
         const userId = decryptAesObj(localStorage.getItem("data")).id
        ScheduleMasterAction.deleteScheduleMasterBulk({ tripIds,userId })
            .then((res) => {
                if (res?.status === 200) {
                    fetchScheduleList();
                    setSelectedTrips(new Set());
                }
                setBulkDeleting(false);
                setBulkDeleteOpen(false);
            })
            .catch(() => { setBulkDeleting(false); setBulkDeleteOpen(false); });
    };

    // ── import file pick ──────────────────────────────────────────────────────
    const handleImportFileChange = (e) => {
        const file = e.target.files[0];
        setImportError(""); setImportFile(null);
        setValidationResults([]); setShowValidation(false); setUploadResult(null);
        if (!file) return;
        if (!ALLOWED_EXCEL_TYPES.includes(file.type)) {
            setImportError("Only Excel files (.xls, .xlsx) are allowed.");
            if (fileInputRef.current) fileInputRef.current.value = ""; return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setImportError("File size must be less than 10MB.");
            if (fileInputRef.current) fileInputRef.current.value = ""; return;
        }
        setImportFile(file);
    };

    // ── GATE 1: validate → GATE 2: upload ────────────────────────────────────
    const handleImportSubmit = async () => {
        if (!importFile) { setImportError("Please select a file."); return; }
        setImporting(true);
        setUploadResult(null);

        try {
            // ── Gate 1: front-end validation ─────────────────────────────────
            const results = await validateExcelFile(importFile);
            const hasErrors = results.some(r => r.status === "error");

            setValidationResults(results);
            setShowValidation(true);

            if (hasErrors) {
                // ❌ Stop here — show front-end errors, don't hit the API
                setImporting(false);
                return;
            }

            // ── Gate 2: all rows valid → call API ─────────────────────────────
            const formData = new FormData();
            formData.append("file", importFile);
            formData.append("userId", decryptAesObj(localStorage.getItem("data")).id)
            const response = await ScheduleMasterAction.uploadScheduleMaster(formData);

            if (response?.status === 200) {
                // Show server result: { successCount, failedRows }
                const result = response?.data?.data || {};
                setUploadResult({
                    successCount: result.successCount || 0,
                    failedRows: Array.isArray(result.failedRows) ? result.failedRows : [],
                });
                // hide front-end validation panel — server result takes over
                setShowValidation(false);
                fetchScheduleList();
            } else {
                setImportError(response?.data?.message || "Upload failed. Please try again.");
            }
        } catch (err) {
            setImportError(err?.message || "Upload failed.");
        } finally {
            setImporting(false);
        }
    };

    const handleImportClose = () => {
        setImportOpen(false); setImportFile(null); setImportError("");
        setValidationResults([]); setShowValidation(false); setUploadResult(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // ── PDF export ────────────────────────────────────────────────────────────
    const getExportData = (type) => {
        if (type !== "pdf") return;
        setExportDataPdf(userlist.map((item, i) => ({
            col1: i + 1, col2: item.vehicleId || "—", col3: item.fplName || "—",
            col4: item.day || "—",
            col5: `${formatTime(item.arrivalTimeAtFpl)} - ${formatTime(item.departureTimeFromFpl)}`,
            col6: `${formatTime(item.arrivalTimeAtDeliveryPoint)} - ${formatTime(item.departureTimeAtDeliveryPoint)}`,
        })));
        setTimeout(() => document.getElementById("pdf-export-btn")?.click(), 100);
    };

    const handleChangePage = (_, p) => {
        setPageNo(p);
        setSelectedTrips(new Set());
    };

    const handleChangeRowsPerPage = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setPageNo(0);
        setSelectedTrips(new Set());
    };

    // ✅ CORRECT - 3 useEffect hooks
    useEffect(() => {

        fetchVehicleList();
        // fetchScheduleList();
        // fetchFplList()
    }, []);

    // useEffect(() => {
    //     setPageNo(0);
    //     setSelectedTrips(new Set());
    //     fetchScheduleList()
    // }, [selectedVehicle]);

    useEffect(() => {
        setPageNo(0);
        setSelectedTrips(new Set());
        fetchScheduleList();
    }, [selectedFpl, selectedVehicle]);


    useEffect(() => {
        fetchScheduleList();
    }, [pageNo, pageSize]);

    // derived
    const failedCount = uploadResult?.failedRows?.length || 0;
    const successCount = uploadResult?.successCount || 0;

    const topViewData = {
        title: `${isFpl ? "FPL" : "Schedule"} Management`,
        subTitleFirst: "Dashboard >", subTitleSecond: "Company >",
        subTitleThird: isFpl ? "FPL" : "Schedule",
        buttonIcon: false, backPath: "/dashboard", backPathSecond: "/dashboard",
        addButtonPath: "/addfpl", addButton: false, editButton: false,
        updateButton: false, updatePermission: [], addButtonPermission: addSitePermission,
        hideBtn: false, export: false, exportPdf: true, exportExcel: false, getExportData,
    };

    return (
        <div className="makeStylesRoot" style={{ position: "relative" }}>
            <LoadingComponent isLoading={isLoading} />

            <Container>
                <Grid style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginTop: "-20px", marginBottom: "12px" }}>

                    <TopView data={topViewData} />


                    <div style={{
                        display: "flex", gap: 5
                    }}>

                        <Button variant="contained" color="primary" startIcon={<CloudUploadIcon />} onClick={() => setImportOpen(true)} style={{ whiteSpace: "nowrap", width: 180 }}>
                            Import Excel
                        </Button>
                        {selectedTrips.size > 0 && (
                            <Button
                                variant="contained"
                                style={{ background: "#f44336", color: "#fff", whiteSpace: "nowrap" }}
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDeleteClick}
                            >
                                Delete {selectedTrips.size} trip{selectedTrips.size !== 1 ? "s" : ""}
                            </Button>
                        )}
                    </div>
                </Grid>

                <div style={{ display: "none" }}>
                    <ExportPdf exportData={exportDataPdf} labelHeader={labelHeader} title="Schedule List" reportName="scheduleList" id="pdf-export-btn" />
                </div>

                <Paper className={classes.paper} id="left">
                    <ScheduleToolbar
                        vehicleList={vehicleList}
                        selectedVehicle={selectedVehicle}
                        setSelectedVehicle={setSelectedVehicle}
                        fplList={fplList}
                        selectedFpl={selectedFpl}
                        setSelectedFpl={setSelectedFpl}
                        selectedCount={selectedTrips.size}
                    />

                    <TableContainer style={{ margin: "1rem", width: "97%", paddingTop: "2px", height: "500px", overflow: "auto" }}>
                        <Table className={classes.table} aria-label="schedule table">
                            <EnhancedTableHead
                                onSelectAllClick={handleSelectAllClick}
                                numSelected={selectedTrips.size}
                                rowCount={visibleRows.length}
                            />
                            <TableBody className="border">
                                {visibleRows.length > 0 ? visibleRows.map((item, index) => {
                                    const dayStyle = DAY_COLOR[item.day] || { bg: "#F5F5F5", color: "#555", border: "#DDD" };
                                    const isSelected = selectedTrips.has(item.id);
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={item.id || index}
                                            selected={isSelected}
                                            style={{ background: isSelected ? "#e3f2fd" : "inherit" }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectClick(item.id)}
                                                />
                                            </TableCell>
                                            <TableCell>{pageNo * pageSize + index + 1}</TableCell>
                                            <TableCell style={{ fontWeight: 600, color: "#333" }}>{item.vehicle.number}</TableCell>
                                            <TableCell><span style={{ color: "#1492E6", fontWeight: 500 }}>{item.fplName || "—"}</span></TableCell>
                                            <TableCell>
                                                <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: dayStyle.bg, color: dayStyle.color, border: `1px solid ${dayStyle.border}`, whiteSpace: "nowrap" }}>
                                                    {item.day || "—"}
                                                </span>
                                            </TableCell>
                                            <TableCell style={{ fontSize: 13, color: "#444" }}>{formatTime(item.arrivalTimeAtFpl)} → {formatTime(item.departureTimeFromFpl)}</TableCell>
                                            <TableCell style={{ fontSize: 13, color: "#444" }}>{formatTime(item.arrivalTimeAtDeliveryPoint)} → {formatTime(item.departureTimeAtDeliveryPoint)}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" className={classes.deleteBtn} onClick={() => handleDeleteClick(item)} title="Delete">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" style={{ color: "#999", padding: "24px" }}>
                                            {selectedVehicle ? `No schedules found for vehicle "${selectedVehicle.number}"` : "No records found"}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={totalCount || userlist.length}
                        rowsPerPage={pageSize}
                        page={pageNo}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            </Container>

            {/* ── Delete Single Confirm ──────────────────────────────────────── */}
            <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Delete Schedule Trip</span>
                        <IconButton size="small" onClick={handleDeleteClose} disabled={deleting}><CloseIcon /></IconButton>
                    </div>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" style={{ color: "#333" }}>
                        Are you sure you want to delete <strong>{deleteTarget?.label}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: "12px 16px", gap: 8 }}>
                    <Button onClick={handleDeleteClose} variant="outlined" disabled={deleting}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" disabled={deleting} style={{ background: "#f44336", color: "#fff" }}>
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Multiple Confirm ────────────────────────────────────── */}
            <Dialog open={bulkDeleteOpen} onClose={handleBulkDeleteClose} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Delete Multiple Trips</span>
                        <IconButton size="small" onClick={handleBulkDeleteClose} disabled={bulkDeleting}><CloseIcon /></IconButton>
                    </div>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" style={{ color: "#333" }}>
                        Are you sure you want to delete <strong>{selectedTrips.size} trip{selectedTrips.size !== 1 ? "s" : ""}</strong>? This action cannot be undone.
                    </Typography>
                    <Typography variant="caption" style={{ color: "#888", display: "block", marginTop: 12 }}>
                        Trip IDs: {Array.from(selectedTrips).join(", ")}
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: "12px 16px", gap: 8 }}>
                    <Button onClick={handleBulkDeleteClose} variant="outlined" disabled={bulkDeleting}>Cancel</Button>
                    <Button onClick={handleBulkDeleteConfirm} variant="contained" disabled={bulkDeleting} style={{ background: "#f44336", color: "#fff" }}>
                        {bulkDeleting ? "Deleting..." : "Delete All"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Import Excel Dialog ────────────────────────────────────────── */}
            <Dialog open={importOpen} onClose={handleImportClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Import Excel File</span>
                        <IconButton size="small" onClick={handleImportClose}><CloseIcon /></IconButton>
                    </div>
                </DialogTitle>

                <DialogContent dividers>

                    {/* ── File picker ────────────────────────────────────────── */}
                    <label htmlFor="import-excel-input" style={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        justifyContent: "center", gap: 10,
                        border: `2px dashed ${importError ? "#f44336" : "#1492E6"}`,
                        borderRadius: 8, padding: "32px 16px", cursor: "pointer",
                        background: importError ? "#fff5f5" : "#f0f7ff", transition: "all 0.2s",
                    }}>
                        <CloudUploadIcon style={{ fontSize: 40, color: importError ? "#f44336" : "#1492E6" }} />
                        <Typography variant="body1" style={{ fontWeight: 600, color: "#333" }}>
                            {importFile ? importFile.name : "Click to choose or drag & drop"}
                        </Typography>
                        <Typography variant="caption" style={{ color: "#888" }}>
                            Accepted: .xls, .xlsx only · Max size: 10MB
                        </Typography>
                        <input
                            id="import-excel-input" ref={fileInputRef} type="file"
                            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            hidden onChange={handleImportFileChange}
                        />
                    </label>

                    {importError && (
                        <Typography variant="caption" style={{ color: "#f44336", display: "block", marginTop: 8, fontWeight: 600 }}>
                            ⚠ {importError}
                        </Typography>
                    )}

                    {importFile && !importError && (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, padding: "8px 12px", background: "#e8f5e9", borderRadius: 6, border: "1px solid #a5d6a7" }}>
                            <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 600 }}>
                                ✓ {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                            </Typography>
                            <IconButton size="small" onClick={() => { setImportFile(null); setImportError(""); setValidationResults([]); setShowValidation(false); setUploadResult(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>
                    )}

                    {/* ── GATE 1: Front-end validation results ───────────────── */}
                    {showValidation && validationResults.length > 0 && !uploadResult && (
                        <div style={{ marginTop: 16 }}>
                            <div style={{ display: "flex", gap: 12, marginBottom: 10, alignItems: "center" }}>
                                <Typography variant="caption" style={{ fontWeight: 700, color: "#333" }}>
                                    Validation Results:
                                </Typography>
                                <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 14, fontWeight: 700, background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }}>
                                    ✓ {validationResults.filter(r => r.status === "valid").length} Valid
                                </span>
                                <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 14, fontWeight: 700, background: "#fff5f5", color: "#f44336", border: "1px solid #ffcdd2" }}>
                                    ✗ {validationResults.filter(r => r.status === "error").length} Errors
                                </span>
                            </div>
                            <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 8 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow style={{ background: "#f5f5f5" }}>
                                            {["Row", "Tanker No", "Status", "Reason / Errors"].map(h => (
                                                <TableCell key={h} style={{ fontWeight: 700, fontSize: 14, color: "#555", whiteSpace: "nowrap", padding: "8px 12px" }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {validationResults.map((row, idx) => (
                                            <TableRow key={idx} style={{ background: row.status === "error" ? "#fff8f8" : "#f9fff9" }}>
                                                <TableCell style={{ fontSize: 14, padding: "6px 12px", color: "#666" }}>{row.rowNum}</TableCell>
                                                <TableCell style={{ fontSize: 14, padding: "6px 12px", fontWeight: 700, color: "#333" }}>{row.tankerNo}</TableCell>
                                                <TableCell style={{ padding: "6px 12px" }}>
                                                    <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: row.status === "valid" ? "#e8f5e9" : "#fff5f5", color: row.status === "valid" ? "#2e7d32" : "#f44336", border: `1px solid ${row.status === "valid" ? "#a5d6a7" : "#ffcdd2"}` }}>
                                                        {row.status === "valid" ? "✓ Valid" : "✗ Error"}
                                                    </span>
                                                </TableCell>
                                                <TableCell style={{ fontSize: 14, padding: "6px 12px", color: row.status === "error" ? "#f44336" : "#2e7d32", maxWidth: 300 }}>
                                                    {row.errors.length > 0 ? row.errors.map((err, ei) => <div key={ei}>• {err}</div>) : row.reason}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* ── GATE 2: Server upload result ───────────────────────── */}
                    {uploadResult && (
                        <div style={{ marginTop: 16 }}>
                            {/* Summary */}
                            <div style={{ display: "flex", gap: 12, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
                                <Typography variant="body2" style={{ fontWeight: 700, color: "#333" }}>
                                    Upload Complete:
                                </Typography>
                                <span style={{ padding: "3px 12px", borderRadius: 12, fontSize: 13, fontWeight: 700, background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }}>
                                    ✓ {successCount} row{successCount !== 1 ? "s" : ""} uploaded
                                </span>
                                {failedCount > 0 && (
                                    <span style={{ padding: "3px 12px", borderRadius: 12, fontSize: 13, fontWeight: 700, background: "#fff5f5", color: "#f44336", border: "1px solid #ffcdd2" }}>
                                        ✗ {failedCount} row{failedCount !== 1 ? "s" : ""} failed
                                    </span>
                                )}
                            </div>

                            {/* Failed rows from server */}
                            {failedCount > 0 && (
                                <>
                                    <Typography variant="caption" style={{ fontWeight: 700, color: "#f44336", marginBottom: 6, display: "block" }}>
                                        Server rejected these rows — fix in your Excel file and re-upload:
                                    </Typography>
                                    <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #ffcdd2", borderRadius: 8 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ fontWeight: 700, fontSize: 13, color: "#c62828", padding: "8px 14px", background: "#fff5f5", whiteSpace: "nowrap" }}>#</TableCell>
                                                    <TableCell style={{ fontWeight: 700, fontSize: 13, color: "#c62828", padding: "8px 14px", background: "#fff5f5" }}>Error</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {uploadResult.failedRows.map((errMsg, idx) => (
                                                    <TableRow key={idx} style={{ background: idx % 2 === 0 ? "#fff8f8" : "#fff" }}>
                                                        <TableCell style={{ fontSize: 13, padding: "5px 14px", color: "#888", whiteSpace: "nowrap" }}>{idx + 1}</TableCell>
                                                        <TableCell style={{ fontSize: 13, padding: "5px 14px", color: "#c62828" }}>{errMsg}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </>
                            )}

                            {/* All success */}
                            {failedCount === 0 && (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 0" }}>
                                    <Typography variant="body1" style={{ color: "#2e7d32", fontWeight: 600 }}>
                                        🎉 All {successCount} rows uploaded successfully!
                                    </Typography>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: "12px 16px", gap: 8 }}>
                    <Button onClick={handleImportClose} variant="outlined" disabled={importing}>
                        {uploadResult ? "Close" : "Cancel"}
                    </Button>

                    {/* Gate 1 errors → Re-upload */}
                    {showValidation && validationResults.some(r => r.status === "error") && !uploadResult && (
                        <Button
                            onClick={() => { setShowValidation(false); setValidationResults([]); setImportFile(null); setImportError(""); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                            variant="contained"
                            style={{ background: "#f44336", color: "#fff" }}
                        >
                            Re-upload File
                        </Button>
                    )}

                    {/* Normal upload button — visible when no result yet and no gate-1 errors blocking */}
                    {!uploadResult && !(showValidation && validationResults.some(r => r.status === "error")) && (
                        <Button
                            onClick={handleImportSubmit}
                            variant="contained"
                            color="primary"
                            disabled={!importFile || !!importError || importing}
                        >
                            {importing ? (showValidation ? "Uploading..." : "Validating...") : "Validate & Upload"}
                        </Button>
                    )}

                    {/* Gate 2 server errors → Re-upload */}
                    {uploadResult && failedCount > 0 && (
                        <Button
                            onClick={() => { setUploadResult(null); setValidationResults([]); setShowValidation(false); setImportFile(null); setImportError(""); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                            variant="contained"
                            style={{ background: "#1492E6", color: "#fff" }}
                        >
                            Re-upload File
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ScheduleMaster;